import { useMemo, useState, MouseEventHandler } from "react";
import mobile from "is-mobile";
import { Box, Button, Flex, Image } from "@chakra-ui/react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import logo from "../../assets/logo.png";
import logoSvg from "../../pages/app/LoggedOut/images/logo.svg";
import gearOutline from "../../assets/gear-outline.svg";
import { Icon, type IconName } from "../../components/Icon/Icon";
import { useStoreData } from "../../lib/store/store";
import { isLoggedIn } from "../../lib/isLoggedIn";
import { AuthenticationDialog } from "../AuthenticationDialog/AuthenticationDialog";
import { ModeSwitch } from "../ModeSwitch/ModeSwitch";
import { useWorkspace } from "../../lib/useWorkspace";

interface NavTab {
  to: string;
  label: string;
  icon: IconName;
}

const LOGGED_IN_TABS: NavTab[] = [
  { to: "/events", label: "home", icon: "logo" },
  { to: "/spark", label: "spark", icon: "spark" },
  { to: "/matches", label: "matches", icon: "chat" },
];

function IconTab({ to, label, icon }: NavTab) {
  return (
    <NavLink to={to} end>
      {({ isActive }) => (
        <Flex
          direction="column"
          align="center"
          justify="center"
          gap="0.25rem"
          color={isActive ? "fg.brand" : "brand.green.dark"}
          fontSize="0.875rem"
          lineHeight="1"
        >
          <Icon name={icon} boxSize="1.5rem" />
          <span>{label}</span>
        </Flex>
      )}
    </NavLink>
  );
}

export function TopNav() {
  const isMobile = useMemo(() => mobile(), []);
  const storeData = useStoreData();
  const loggedIn = useMemo(() => isLoggedIn(storeData), [storeData]);
  const [loginDialogShown, setLoginDialogShown] = useState(false);
  const [registerDialogShown, setRegisterDialogShown] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // Admins and hosts get the ModeSwitch beside the gear; for everyone else
  // the nav is unchanged.
  const workspace = useWorkspace();

  /** The gear, with the ModeSwitch to its left for admins and hosts. */
  const withModeSwitch = (gear: React.ReactNode) =>
    workspace ? (
      <Flex align="center" gap="4">
        <ModeSwitch workspace={workspace} side="app" />
        {gear}
      </Flex>
    ) : (
      gear
    );

  const openSettings = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate("/settings", {
      state: { animate: true, from: location.pathname + location.search },
    });
  };

  const onMarketingNavClick: MouseEventHandler<HTMLAnchorElement> = (e) => {
    const url = new URL(e.currentTarget.href);
    if (url.pathname === "/") {
      const el = document.getElementById(url.hash.substring(1));
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
        e.preventDefault();
      }
    }
  };

  const authDialogs = (
    <>
      <AuthenticationDialog
        title="SIGN IN"
        confirmLabel="Sign in"
        isShown={loginDialogShown}
        onClose={() => setLoginDialogShown(false)}
      />
      <AuthenticationDialog
        title="SIGN UP"
        confirmLabel="Sign up"
        isShown={registerDialogShown}
        onClose={() => setRegisterDialogShown(false)}
      />
    </>
  );

  if (isMobile) {
    return (
      <>
        <Flex
          as="header"
          align="center"
          justify="space-between"
          w="100%"
          h="3rem"
          px="1rem"
          zIndex={20}
          bg="brand.green.light"
        >
          <NavLink to={loggedIn ? "/events" : "/"}>
            <Image src={logoSvg} alt="Green Flag" h="1.5rem" w="auto" filter="brightness(0) invert(1)" />
          </NavLink>
          {loggedIn ? (
            withModeSwitch(
              <NavLink to="/settings" onClick={openSettings} aria-label="Settings">
                <Image src={gearOutline} alt="" w="1.25rem" h="1.25rem" filter="brightness(0) invert(1)" />
              </NavLink>,
            )
          ) : (
            <Flex align="center" gap="1rem">
              <Box
                as="button"
                color="white"
                fontSize="0.875rem"
                opacity={0.85}
                onClick={() => setLoginDialogShown(true)}
              >
                Sign In
              </Box>
              <Button
                size="sm"
                minH="2rem"
                h="2rem"
                bg="white"
                color="brand.green.dark"
                onClick={() => setRegisterDialogShown(true)}
              >
                Sign Up
              </Button>
            </Flex>
          )}
        </Flex>
        {authDialogs}
      </>
    );
  }

  if (loggedIn) {
    return (
      <Flex
        as="header"
        align="center"
        justify="space-between"
        w="100%"
        h="4rem"
        px="1.125rem"
        position="sticky"
        top={0}
        zIndex={20}
        bg="white"
        boxShadow="0 2px 4px rgba(0, 0, 0, 0.08)"
      >
        <NavLink to="/events">
          <Image src={logo} alt="Green Flag" h="35px" w="auto" />
        </NavLink>
        <Flex as="nav" gap="3rem" align="center">
          {LOGGED_IN_TABS.map((tab) => (
            <IconTab key={tab.to} {...tab} />
          ))}
        </Flex>
        {withModeSwitch(
          <NavLink to="/settings" onClick={openSettings} aria-label="Settings">
            <Image src={gearOutline} alt="" w="1.25rem" h="1.25rem" />
          </NavLink>,
        )}
      </Flex>
    );
  }

  return (
    <>
      <Flex
        as="header"
        align="stretch"
        justify="space-between"
        w="100%"
        h="4rem"
        bg="white"
        zIndex={20}
      >
        <NavLink to="/" style={{ display: "flex", alignItems: "center", padding: "0 1.125rem" }}>
          <Image src={logo} alt="Green Flag" h="35px" w="auto" />
        </NavLink>
        <Flex as="nav" align="center" gap="1.85rem" px="1.85rem">
          <NavLink to="/events" onClick={onMarketingNavClick}>
            {({ isActive }) => (
              <Box fontWeight={isActive ? "bold" : undefined} color={isActive ? "brand.green.primary" : "inherit"}>
                EVENTS
              </Box>
            )}
          </NavLink>
          <Box css={{ "@media (max-width: 42rem)": { display: "none" } }}>
            <NavLink to="/team" onClick={onMarketingNavClick}>
              {({ isActive }) => (
                <Box fontWeight={isActive ? "bold" : undefined} color={isActive ? "brand.green.primary" : "inherit"}>
                  TEAM
                </Box>
              )}
            </NavLink>
          </Box>
          <Box css={{ "@media (max-width: 42rem)": { display: "none" } }}>
            <NavLink to="/contact" onClick={onMarketingNavClick}>
              {({ isActive }) => (
                <Box fontWeight={isActive ? "bold" : undefined} color={isActive ? "brand.green.primary" : "inherit"}>
                  CONTACT
                </Box>
              )}
            </NavLink>
          </Box>
          <Box
            as="button"
            onClick={() => setLoginDialogShown(true)}
            css={{ "@media (max-width: 42rem)": { display: "none" } }}
          >
            Sign In
          </Box>
          <Button
            bg="brand.green.light"
            color="brand.green.dark"
            alignSelf="center"
            onClick={() => setRegisterDialogShown(true)}
          >
            Sign Up
          </Button>
        </Flex>
      </Flex>
      {authDialogs}
    </>
  );
}
